import * as THREE from "three";
import { DRACOLoader } from "three/addons/loaders/DRACOLoader.js";
import { GLTFLoader } from "three/addons/loaders/GLTFLoader.js";
import { STLLoader } from "three/addons/loaders/STLLoader.js";
import { STLExporter } from "three/addons/exporters/STLExporter.js";
import * as BufferGeometryUtils from "three/addons/utils/BufferGeometryUtils.js";
import { getColorFromToothNumber } from "./TeethColors";
import {
  EXTR_TAG,
  CUT_TAG,
  TOOTH_TAG,
  STL_TAG,
  setAllModels,
  initStlViewer,
  applyBorderPoints,
  applyAnchorPoints,
  fillIntraoralList,
  viewMode,
} from "./IntraoralManager";

//#region Fields

const gingivaName = "gingiva";
const gingivaNames = ["gingiva", "upper_gingiva", "lower_gingiva"];
const gingivaColor = "#ffb6c1";
const pongMaterial = new THREE.MeshPhongMaterial({
  // color: gingivaColor,
  flatShading: false,
  // flatShading: true,
  specular: 0x050505,
  shininess: 200,
  side: 2,
  polygonOffset: true,
  // transparent: true,
  // polygonOffsetUnit: 1,
  // wireframe: false,
});

const gingiva_extr_meshes = ["upper_extr_mesh.stl", "lower_extr_mesh.stl"];
const gingiva_closed_meshes = [
  "upper_extr_closed_mesh.stl",
  "lower_extr_closed_mesh.stl",
];

const addedSuffix = "added_";
const upperSuffix = "upper_";
const lowerSuffix = "lower_";

//#endregion

async function processIntraoralZip() {
  console.time("intraoral download");
  let zip = await fetchZipAsync(vueApp.intraoralZipUrl);
  // let zip = await fetchZipAsync("/demo/0segmentation-standalone-mobile.zip");
  console.timeEnd("intraoral download");

  if (zip) {
    console.time("intraoral unzip");

    // if (vueApp.isReset)  // TODO
    let stlModels = [];
    let allKeys = Object.keys(zip.files);
    console.log("intraol zip allKeys", allKeys);

    let toothKeys = allKeys.filter(
      (key) =>
        key.startsWith(EXTR_TAG) &&
        key.endsWith(STL_TAG) &&
        key.includes(TOOTH_TAG)
    );
    if (toothKeys.length == 0) {
      toothKeys = allKeys.filter(
        (key) => key.endsWith(STL_TAG) && key.startsWith(TOOTH_TAG)
      );
    }
    console.log("intraol zip toothKeys", toothKeys);

    console.time("intraoral unzip teeth");
    for (const toothKey of toothKeys) {
      let zipFile = zip.file(toothKey);
      let buffer = await zipFile.async("arraybuffer");
      let basePath = toothKey.replace(EXTR_TAG, "");
      let cutPath = CUT_TAG + basePath; // toothKey.replace(EXTR_TAG, CUT_TAG);
      let geometry = getGeometryFromStl(buffer);
      geometry.computeBoundingBox();
      let elementName = basePath.replace(STL_TAG, "");
      let model = {
        name: elementName,
        geometry: geometry,
        baseGeometry: geometry,
        savedGeometry: geometry,
        cutZipFile: zip.file(cutPath),
        isTooth: true,
        isGingiva: false,
        isUpper:
          elementName.startsWith("tooth_1") ||
          elementName.startsWith("tooth_2"),
        filename: toothKey,
      };
      model.isMaxilla = model.isUpper;
      stlModels.push(model);
    }
    console.timeEnd("intraoral unzip teeth");

    console.time("intraoral unzip gingivae");
    let gingivaKeys = allKeys.filter((key) =>
      gingiva_closed_meshes.includes(key)
    );
    if (!gingivaKeys.length)
      gingivaKeys = allKeys.filter((key) => gingiva_extr_meshes.includes(key));
    console.log("intraol zip gingivaKeys", gingivaKeys);

    for (const gingivaKey of gingivaKeys) {
      let zipFile = zip.file(gingivaKey);
      if (zipFile) {
        let buffer = await zipFile.async("arraybuffer");
        let geometry = getGeometryFromStl(buffer);
        geometry.computeBoundingBox();
        let isUpper = gingivaKey.includes(upperSuffix);
        let model = {
          name: isUpper ? "upper_gingiva" : "lower_gingiva",
          geometry: geometry,
          baseGeometry: geometry,
          savedGeometry: geometry,
          isUpper: isUpper,
          isTooth: false,
          isGingiva: true,
          filename: gingivaKey,
        };
        model.isMaxilla = model.isUpper;
        stlModels.push(model);
      }
    }
    console.timeEnd("intraoral unzip gingivae");
    console.log("intraoral stlModels", stlModels);

    console.time("intraoral unzip glb");
    let upperBuffer = await getArrayBufferFromZip(zip, "upper_segmented.glb");
    let lowerBuffer = await getArrayBufferFromZip(zip, "lower_segmented.glb");
    let upperGlbModels = await processGlbBufferAsync(upperBuffer, true);
    let lowerGlbModels = await processGlbBufferAsync(lowerBuffer, false);
    let glbModels = upperGlbModels.concat(lowerGlbModels);
    console.timeEnd("intraoral unzip glb");
    // let glbModels = [];
    console.log("intraol glbModels", glbModels);
    console.timeEnd("intraoral unzip");

    console.time("intraoral apply jsons");

    let settings = await loadSettingsFromZip(zip);
    console.log("intraoral settings", settings);

    let allModels = processZipModels(stlModels, glbModels, settings);
    console.log("intraoral allModels", allModels);

    let upperGingiva = allModels.find((g) => g.isUpper);
    let lowerGingiva = allModels.find((g) => !g.isUpper);

    if (!upperGingiva && !lowerGingiva) {
      $("#loaderText").html(`Load error. Please contact support@cephx.com`);
    } else {
      viewMode.isDouble = !!upperGingiva && !!lowerGingiva;
      // upperGingiva !== null && lowerGingiva !== null;
      setAllModels(allModels);
      if (upperGingiva) {
        await loadBorderPointsFromZip(zip, "upper_border_points.json", true);
        await loadAnchorPointsFromZip(zip, "upper_extr_anchors.json", true);
      }
      if (lowerGingiva) {
        await loadBorderPointsFromZip(zip, "lower_border_points.json", false);
        await loadAnchorPointsFromZip(zip, "lower_extr_anchors.json", false);
      }
      initStlViewer();
    }
  } else {
    console.log("FAIL TO GET INTRAORAL ZIP", vueApp.intraoralZipUrl);
  }

  console.timeEnd("intraoral apply jsons");
}

async function processGlbBufferAsync(buffer, isUpper) {
  let glbModels = [];
  if (buffer) {
    let gltfLoader = new GLTFLoader();
    let result = await gltfLoader.parseAsync(buffer, "");
    let world = result.scene.children[0];
    world.children.forEach((element) => {
      if (element.type === "Mesh") {
        let geometry = element.geometry;
        geometry.computeBoundingBox();
        let elementName = element.name.replace("stl", "").replace(".", "");
        let toothNumber = +elementName.replace("tooth_", "");

        let isGingiva = gingivaNames.includes(elementName);
        if (isGingiva && elementName.toLowerCase() == gingivaName) {
          elementName = isUpper
            ? `upper_${gingivaName}`
            : `lower_${gingivaName}`;
        }
        const model = {
          name: elementName,
          number: toothNumber,
          isGingiva: isGingiva,
          isUpper: isUpper,
          isMaxilla: isUpper,
          isTooth: !isGingiva,
          geometry: geometry,
          baseGeometry: geometry,
          savedGeometry: geometry,
          baseNumber: toothNumber,
          glbNumber: toothNumber,
          filename: element.name,
          isAdded: false,
        };
        // console.log(model);
        glbModels.push(model);
      }
    });
  }
  return glbModels;
}

function processZipModels(stlModels, glbModels, settings) {
  let allModels = [];

  glbModels.forEach((glbModel) => {
    let stlModel = stlModels.find((m) => m.name == glbModel.name);
    if (stlModel) {
      if (stlModel.isTooth) {
        glbModel.savedGeometry = stlModel.savedGeometry;
        glbModel.cutZipFile = stlModel.cutZipFile;
      }
    }
    allModels.push(glbModel);
  });

  stlModels.forEach((stlModel) => {
    let elementName = stlModel.name;
    let existedGlbModel = glbModels.find((m) => m.name == elementName);
    let isAdded = !existedGlbModel; // elementName.includes(addedSuffix);
    if (isAdded) {
      stlModel.isAdded = true;
      allModels.push(stlModel);
      if (stlModel.isTooth) {
        console.log("intraoral added", elementName);
        stlModel.number = +elementName.replace("tooth_", "");
        stlModel.baseNumber = stlModel.number;
        stlModel.glbNumber = 0;
      }
    } else if (existedGlbModel.isGingiva) {
      existedGlbModel.filledGeometry = stlModel.geometry;
    }
  });

  allModels.forEach((model) => {
    let color = gingivaColor;
    if (!model.isGingiva) {
      color = getColorFromToothNumber(model.number);
    }
    model.color = color;
    model.opacity = 0.01;
    model.isVisible = true;
    model.isDeleted = false;
    model.filename = null;

    let material = pongMaterial.clone();
    material.color = new THREE.Color(color);
    material.polygonOffsetUnit = 1;
    // https://stackoverflow.com/questions/25165013/three-js-overlapping-layers-flickering
    material.polygonOffsetFactor = model.isGingiva ? 9 : 3;

    let mesh = new THREE.Mesh(model.savedGeometry, material);
    mesh.castShadow = false;
    mesh.receiveShadow = false;
    mesh.name = model.name;

    model.mesh = mesh;
    model.mainMesh = mesh;

    if (model.filledGeometry) {
      let filledMesh = new THREE.Mesh(model.filledGeometry, material);
      filledMesh.castShadow = false;
      filledMesh.receiveShadow = false;
      filledMesh.name = model.name;
      model.filledMesh = filledMesh;
    }
  });
  applySettings(allModels, settings);
  return allModels;
}

async function fetchZipAsync(zipUrl) {
  let zip = null;
  try {
    let response = await fetch(zipUrl, { cache: "no-store" });
    if (response.status == 200) {
      zip = await JSZip.loadAsync(response.arrayBuffer());
    } else {
      console.log(`Error response for ${vueApp.intraoralZipUrl}.`, response);
    }
  } catch (error) {
    console.log(`Error in fetchZipAsync. Error: ${error}`);
  }
  return zip;
}

async function getArrayBufferFromZip(zip, fileName) {
  let buffer = null;
  let zipFile = zip.file(fileName);
  if (zipFile) {
    buffer = await zipFile.async("arraybuffer");
  }
  return buffer;
}

async function loadSettingsFromZip(zip) {
  let settingsObj = null;
  let fileName = "intraoral_settings.json";
  let settingsFile = zip.file(fileName);
  if (settingsFile) {
    let text = await settingsFile.async("string");
    try {
      settingsObj = JSON.parse(text);
    } catch (e) {
      console.log(`Invalid JSON: ${fileName}! Error: ${e}`);
    }
  }
  return settingsObj;
}

function applySettings(models, settings) {
  models.sort((a, b) => a.name.localeCompare(b.name));
  if (settings && settings.teeth) {
    for (let name in settings.teeth) {
      let model = models.find((m) => m.name === name);
      //getModelByName(name);
      if (model) {
        let toothSettings = settings.teeth[name];
        if (typeof toothSettings.isAdded !== "undefined" && toothSettings.isAdded)
          model.isAdded = toothSettings.isAdded;

        if (typeof toothSettings.glbNumber !== "undefined") {
          model.glbNumber = settings.teeth[name].glbNumber;
          if (model.glbNumber && !model.isAdded) {
            model.isAdded = false;
          }
        }

        model.isSaved = settings.teeth[name].isSaved;
        model.isDeleted = model.isDeleted || settings.teeth[name].isDeleted;

        if (typeof toothSettings.isUpper !== "undefined")
          model.isUpper = model.isMaxilla = toothSettings.isUpper;
      }
    }

    // OBSOLETE ?
    let settingsKeys = Object.keys(settings.teeth);
    console.log("settingsKeys", settingsKeys);
    models
      .map((x) => x)
      .forEach((model) => {
        let settingsIndex = settingsKeys.indexOf(model.name);
        if (model.isTooth && settingsIndex < 0) {
          model.isDeleted = true;
          // let modelIndex = models.indexOf(model);
          //  models.splice(modelIndex, 1);
        }
      });
  }
}

async function loadBorderPointsFromZip(zip, fileName, isUpper) {
  let basePointsFile = zip.file(fileName);
  // console.log("loadBorderPointsFromZip", fileName, basePointsFile);
  if (basePointsFile) {
    let text = await basePointsFile.async("string");
    try {
      let borderPoints = JSON.parse(text);
      applyBorderPoints(borderPoints, true, isUpper);
    } catch (e) {
      console.log(`Invalid JSON: ${fileName}! Error: ${e}`);
    }
  } else {
    console.log(`No such file in zip response - ${fileName}.`);
  }

  let savedPointsFile = zip.file("saved_" + fileName);
  // console.log("savedPointsFile", savedPointsFile);
  if (savedPointsFile && !vueApp.isReset) {
    let text = await savedPointsFile.async("string");
    try {
      let borderPoints = JSON.parse(text);
      applyBorderPoints(borderPoints, false, isUpper);
    } catch (e) {
      console.log(`Invalid JSON: ${savedPointsFile}! Error: ${e}`);
    }
  }
}

async function loadAnchorPointsFromZip(zip, fileName, isUpper) {
  let baseAnchorsFile = zip.file(fileName);
  // console.log("loadAnchorPointsFromZip", fileName, baseAnchorsFile);
  if (baseAnchorsFile) {
    let text = await baseAnchorsFile.async("string");
    try {
      let anchorPoints = JSON.parse(text);
      applyAnchorPoints(anchorPoints, true, isUpper);
    } catch (e) {
      console.log(`Invalid JSON: ${fileName}! Error: ${e}`);
    }
  }

  let savedName = "saved_" + fileName;
  let savedAnchorsFile = zip.file(savedName);
  console.log("savedAnchorsFile", savedName, savedAnchorsFile);
  if (savedAnchorsFile && !vueApp.isReset) {
    let text = await savedAnchorsFile.async("string");
    try {
      let anchorPoints = JSON.parse(text);
      applyAnchorPoints(anchorPoints, false, isUpper);
    } catch (e) {
      console.log(`Invalid JSON: ${fileName}! Error: ${e}`);
    }
  }
}

//#region  Obsolete

async function fetchZip2(callback) {
  var promise = new JSZip.external.Promise(function (resolve, reject) {
    JSZipUtils.getBinaryContent(vueApp.intraoralZipUrl, {
      progress: function (event) {
        let percent = Math.ceil(event.percent * 0.99);
        console.log(`intraoral zip ${percent} %`);
      },
      callback: function (err, zipData) {
        if (err) {
          console.log(err);
        } else {
          console.log(`intraoral zip downloaded, ${vueApp.intraoralZipUrl}`);
          resolve(zipData);
          // callback(zipData);
        }
      },
    });
  });

  promise
    .then(JSZip.loadAsync)
    .then(function (zip) {
      callback(zip);
    })
    .then(
      function success(text) {
        console.log("intraoral zip success");
      },
      function error(e) {
        if (callback) callback(null);
      }
    );
}

async function fetchGlbBuffer(glbUrl, callback) {
  if (!glbUrl) {
    callback(null);
    return;
  }
  fetch(glbUrl)
    .then(async (response) => {
      // console.log(response);
      if (response.status == 200) {
        let buffer = await response.arrayBuffer();
        callback(buffer);
      } else callback(null);
    })
    .catch((error) => {
      console.log(`Error response for ${glbUrl}. Error: ${error}`);
      callback(null);
    });
}

async function fetchPointsJson(jsonUrl) {
  if (!jsonUrl) return;
  fetch(jsonUrl)
    .then((response) => {
      response.text().then((text) => {
        try {
          let borderPoints = JSON.parse(text);
          applyBorderPoints(borderPoints);
        } catch (e) {
          console.log(`Invalid JSON: ${jsonUrl}! Error: ${e}`);
        }
      });
    })
    .catch((error) => {
      console.log(`Error response for ${jsonUrl}. Error: ${error}`);
    });
}

//#endregion

//#region Parsing

function getGeometryFromStl(arrayBuffer, callback) {
  let stlLoader = new STLLoader();
  const geometry = stlLoader.parse(arrayBuffer);
  if (callback) callback(geometry);
  return geometry;
}

function getGeometryFromGlb(arrayBuffer, callback) {
  let gltfLoader = new GLTFLoader();
  gltfLoader.parse(arrayBuffer, "", function (result) {
    let world = result.scene.children[0];
    let geometries = [];
    world.children.forEach((element) => {
      if (element.type === "Mesh") {
        geometries.push(element.geometry);
      }
    });

    let modelGeometry = getGMergedGeometry(geometries);
    if (callback) callback(modelGeometry);
  });
}

function getGeometryFromDrc(arrayBuffer, callback) {
  // <script src="https://www.gstatic.com/draco/versioned/decoders/1.4.1/draco_wasm_wrapper.js"></script>
  let dracoLoader = new DRACOLoader();
  dracoLoader.setDecoderPath(
    "https://www.gstatic.com/draco/versioned/decoders/1.4.1/"
  );

  dracoLoader.parse(arrayBuffer, callback);
}

function getMergedGeometry(meshes) {
  let modelGeometry = null;
  if (meshes && meshes.length) {
    let geometries = [];
    meshes.forEach((b) => {
      let clone = b.clone();
      clone.updateMatrixWorld();

      let geometry = clone.geometry.clone().applyMatrix4(clone.matrixWorld);
      if (geometry.index) geometry = geometry.toNonIndexed();
      if (geometry.attributes) {
        geometry.deleteAttribute("color");
        geometry.deleteAttribute("uv");
        // geometry.deleteAttribute("normal");
        // geometry.computeVertexNormals();
        // geometry.computeBoundingBox();
        geometry.attributes.position.gpuType = 1015;
        geometry.attributes.normal.gpuType = 1015;
      }

      geometries.push(geometry);
    });

    modelGeometry = BufferGeometryUtils.mergeGeometries(geometries, true);
    modelGeometry.deleteAttribute("normal");
    modelGeometry = BufferGeometryUtils.mergeVertices(modelGeometry); // , 1
    modelGeometry.computeVertexNormals();
  }
  return modelGeometry;
}

function getGMergedGeometry(mgeometries) {
  let modelGeometry = null;
  if (mgeometries && mgeometries.length) {
    let geometries = [];
    mgeometries.forEach((mgeometry) => {
      let geometry = mgeometry.clone(); //.applyMatrix4(clone.matrixWorld);
      if (geometry.index) geometry = geometry.toNonIndexed();
      if (geometry.attributes) {
        geometry.deleteAttribute("color");
        geometry.deleteAttribute("uv");
        // geometry.deleteAttribute("normal");
        // geometry.computeVertexNormals();
        // geometry.computeBoundingBox();
        if (geometry.attributes.position)
          geometry.attributes.position.gpuType = 1015;
        if (geometry.attributes.normal)
          geometry.attributes.normal.gpuType = 1015;
      }
      geometries.push(geometry);
    });

    modelGeometry = BufferGeometryUtils.mergeGeometries(geometries, true);
    modelGeometry.deleteAttribute("normal");
    modelGeometry = BufferGeometryUtils.mergeVertices(modelGeometry); // , 1
    modelGeometry.computeVertexNormals();
  }
  return modelGeometry;
}

function exportMeshToStlBlob(mesh) {
  const stlExporter = new STLExporter();
  // const data = stlExporter.parse(mesh);
  // const blob = new Blob([data], { type: 'text/plain' });
  const data = stlExporter.parse(mesh, { binary: true });
  const blob = new Blob([data], { type: "application/octet-stream" });
  return blob;
}

//#endregion

export {
  processIntraoralZip,
  getGeometryFromStl,
  getGeometryFromGlb,
  getGeometryFromDrc,
  getMergedGeometry,
  exportMeshToStlBlob,
};
